import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';

function HaulerSetup() {
  const [status, setStatus] = useState('loading'); // loading | ready | saving | success | error
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState('');
  const [errorKind, setErrorKind] = useState(''); // '' | 'expired'
  const [haulerEmail, setHaulerEmail] = useState('');

  useEffect(function() {
    const hash = window.location.hash.slice(1);
    const params = new URLSearchParams(hash);

    // Supabase surfaces auth failures as hash error params (e.g. after scanner pre-consumption).
    const errorCode = params.get('error_code');
    if (errorCode || params.get('error')) {
      setStatus('error');
      if (errorCode === 'otp_expired') {
        setErrorKind('expired');
        setMessage('This setup link has already been used or expired. Please contact Tambo and we\'ll send you a new one right away.');
      } else {
        const desc = params.get('error_description');
        setMessage(desc ? desc.replace(/\+/g, ' ') : 'Setup link error. Please contact Tambo support.');
      }
      return;
    }

    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token') || '';
    const type = params.get('type');

    if (type !== 'recovery' || !accessToken) {
      setStatus('error');
      setMessage('Invalid or missing setup link. Please contact Tambo support to request a new one.');
      return;
    }

    // Decode the JWT payload (base64url) to extract the intended sub (user ID) and email.
    // This is a read-only decode — verification is handled by Supabase on the server.
    let tokenUserId = null;
    let tokenEmail = null;
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      tokenUserId = payload.sub;
      tokenEmail = payload.email || null;
    } catch (_) {
      setStatus('error');
      setMessage('Malformed setup link. Please contact Tambo support.');
      return;
    }

    if (!tokenUserId) {
      setStatus('error');
      setMessage('Setup link is missing user information. Please contact Tambo support.');
      return;
    }

    // Sign out ANY existing session first — prevents silently evicting an already-logged-in
    // user's session by overwriting it. After signOut the slate is clean for setSession.
    supabase.auth.signOut().finally(function() {
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(function(result) {
          if (result.error) {
            setStatus('error');
            setMessage('This setup link has expired. Please contact Tambo support to request a new one.');
            return;
          }

          // Guard: the session Supabase established must be for the same user the token named.
          const sessionUserId = result.data?.user?.id;
          if (!sessionUserId || sessionUserId !== tokenUserId) {
            supabase.auth.signOut();
            setStatus('error');
            setMessage('Session mismatch detected. This setup link may have been tampered with. Please contact Tambo support.');
            return;
          }

          setHaulerEmail(result.data.user.email || tokenEmail || '');
          setStatus('ready');
        });
    });
  }, []);

  async function handleSetPassword() {
    if (!password) { setMessage('Please enter a password'); return; }
    if (password.length < 8) { setMessage('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setMessage('Passwords do not match'); return; }

    setStatus('saving');
    setMessage('');

    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setStatus('ready');
      setMessage(error.message);
      return;
    }

    // Sign out the recovery session immediately — the hauler should sign in with their new
    // password to confirm it works. Leaving the recovery session active is a security risk.
    await supabase.auth.signOut();
    setStatus('success');
    setTimeout(function() { window.location.href = '/hauler-login'; }, 2500);
  }

  const wrap = {
    minHeight: '100vh', background: '#FAFAF8', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', padding: '32px 20px', fontFamily: 'sans-serif',
  };
  const card = {
    background: '#fff', borderRadius: 14, padding: '36px 32px', width: '100%', maxWidth: 420,
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
  };
  const logo = { fontSize: 26, fontWeight: 800, color: '#0D0D0D', marginBottom: 28, textAlign: 'center' };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' };
  const inputStyle = { width: '100%', padding: '10px 12px', border: '1.5px solid #E0E0DA', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };
  const btn = { width: '100%', padding: '13px 0', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 9, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 8 };

  if (status === 'loading') {
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={logo}>Tam<span style={{ color: '#2EA865' }}>bo</span></div>
          <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Verifying your setup link…</div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={logo}>Tam<span style={{ color: '#2EA865' }}>bo</span></div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#111', marginBottom: 8 }}>Password set!</div>
            <div style={{ fontSize: 14, color: '#6b7280' }}>Taking you to the sign-in page…</div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={logo}>Tam<span style={{ color: '#2EA865' }}>bo</span></div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>{errorKind === 'expired' ? '🔗' : '⚠️'}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111', marginBottom: 10 }}>
              {errorKind === 'expired' ? 'Link expired or already used' : 'Setup link problem'}
            </div>
            <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, marginBottom: 20 }}>{message}</div>
            <div style={{ fontSize: 13, color: '#9ca3af' }}>
              Call or text us at <a href="tel:+19562263903" style={{ color: '#1A7A4A', fontWeight: 600, textDecoration: 'none' }}>(956) 226-3903</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={logo}>Tam<span style={{ color: '#2EA865' }}>bo</span></div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#111', marginBottom: 6, textAlign: 'center' }}>Create your password</div>
        {haulerEmail && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <span style={{ fontSize: 12, background: '#f0fdf4', color: '#166534', padding: '4px 12px', borderRadius: 20, fontWeight: 600 }}>
              {haulerEmail}
            </span>
          </div>
        )}
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 24, textAlign: 'center' }}>
          Choose a password to secure your Tambo hauler account.
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>New Password</label>
          <div style={{ position: 'relative' }}>
            <input
              style={{ ...inputStyle, paddingRight: 40 }}
              type={showPwd ? 'text' : 'password'}
              placeholder="At least 8 characters"
              value={password}
              onChange={function(e) { setPassword(e.target.value); setMessage(''); }}
              onKeyDown={function(e) { if (e.key === 'Enter') handleSetPassword(); }}
            />
            <button type="button" onClick={function() { setShowPwd(function(v) { return !v; }); }} tabIndex={-1} aria-label={showPwd ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9ca3af', lineHeight: 0 }}>
              {showPwd
                ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Confirm Password</label>
          <div style={{ position: 'relative' }}>
            <input
              style={{ ...inputStyle, paddingRight: 40 }}
              type={showConfirm ? 'text' : 'password'}
              placeholder="Repeat your password"
              value={confirm}
              onChange={function(e) { setConfirm(e.target.value); setMessage(''); }}
              onKeyDown={function(e) { if (e.key === 'Enter') handleSetPassword(); }}
            />
            <button type="button" onClick={function() { setShowConfirm(function(v) { return !v; }); }} tabIndex={-1} aria-label={showConfirm ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9ca3af', lineHeight: 0 }}>
              {showConfirm
                ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
        </div>

        {message && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>
            {message}
          </div>
        )}

        <button
          style={{ ...btn, opacity: status === 'saving' ? 0.7 : 1, cursor: status === 'saving' ? 'not-allowed' : 'pointer' }}
          onClick={handleSetPassword}
          disabled={status === 'saving'}
        >
          {status === 'saving' ? 'Setting password…' : 'Set Password & Sign In'}
        </button>
      </div>
    </div>
  );
}

export default HaulerSetup;

export function ResetPasswordPage() {
  const [status, setStatus] = useState('loading');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const role = new URLSearchParams(window.location.search).get('role') || 'client';
  const successUrl = role === 'hauler' ? '/hauler-login' : role === 'admin' ? '/admin' : '/login';

  useEffect(function() {
    const hash = window.location.hash.slice(1);
    const params = new URLSearchParams(hash);

    const errorCode = params.get('error_code');
    if (errorCode || params.get('error')) {
      setStatus('error');
      if (errorCode === 'otp_expired') {
        setMessage('This reset link has already been used or expired. Please use "Forgot password?" on the login page to get a new one.');
      } else {
        const desc = params.get('error_description');
        setMessage(desc ? desc.replace(/\+/g, ' ') : 'Reset link error. Please request a new one from the login page.');
      }
      return;
    }

    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token') || '';
    const type = params.get('type');

    if (type !== 'recovery' || !accessToken) {
      setStatus('error');
      setMessage('Invalid or missing reset link. Please use the "Forgot password?" link from the login page.');
      return;
    }

    let tokenUserId = null;
    let tokenEmail = null;
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      tokenUserId = payload.sub;
      tokenEmail = payload.email || null;
    } catch (_) {
      setStatus('error');
      setMessage('Malformed reset link. Please request a new one from the login page.');
      return;
    }

    if (!tokenUserId) {
      setStatus('error');
      setMessage('Reset link is missing user information. Please request a new one from the login page.');
      return;
    }

    supabase.auth.signOut().finally(function() {
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(function(result) {
          if (result.error) {
            setStatus('error');
            setMessage('This reset link has expired. Please request a new one from the login page.');
            return;
          }

          const sessionUserId = result.data?.user?.id;
          if (!sessionUserId || sessionUserId !== tokenUserId) {
            supabase.auth.signOut();
            setStatus('error');
            setMessage('Session mismatch detected. This reset link may have been tampered with. Please request a new one.');
            return;
          }

          setUserEmail(result.data.user.email || tokenEmail || '');
          setStatus('ready');
        });
    });
  }, []);

  async function handleSetPassword() {
    if (!password) { setMessage('Please enter a password'); return; }
    if (password.length < 8) { setMessage('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setMessage('Passwords do not match'); return; }

    setStatus('saving');
    setMessage('');

    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setStatus('ready');
      setMessage(error.message);
      return;
    }

    await supabase.auth.signOut();
    setStatus('success');
    setTimeout(function() { window.location.href = successUrl; }, 2500);
  }

  const wrap = {
    minHeight: '100vh', background: '#FAFAF8', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', padding: '32px 20px', fontFamily: 'sans-serif',
  };
  const card = {
    background: '#fff', borderRadius: 14, padding: '36px 32px', width: '100%', maxWidth: 420,
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
  };
  const logo = { fontSize: 26, fontWeight: 800, color: '#0D0D0D', marginBottom: 28, textAlign: 'center' };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.06em' };
  const inputStyle = { width: '100%', padding: '10px 12px', border: '1.5px solid #E0E0DA', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };
  const btn = { width: '100%', padding: '13px 0', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 9, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 8 };

  if (status === 'loading') {
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={logo}>Tam<span style={{ color: '#2EA865' }}>bo</span></div>
          <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Verifying your reset link…</div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={logo}>Tam<span style={{ color: '#2EA865' }}>bo</span></div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#111', marginBottom: 8 }}>Password updated!</div>
            <div style={{ fontSize: 14, color: '#6b7280' }}>Taking you to the sign-in page…</div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    const isExpired = message.includes('already been used or expired');
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={logo}>Tam<span style={{ color: '#2EA865' }}>bo</span></div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>{isExpired ? '🔗' : '⚠️'}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111', marginBottom: 10 }}>
              {isExpired ? 'Link expired or already used' : 'Reset link problem'}
            </div>
            <div style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, marginBottom: 20 }}>{message}</div>
            {isExpired && (
              <a href={successUrl} style={{ display: 'inline-block', marginBottom: 16, padding: '10px 24px', background: '#1A7A4A', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                Back to sign-in page
              </a>
            )}
            <div style={{ fontSize: 13, color: '#9ca3af' }}>
              Need help? Call <a href="tel:+19562263903" style={{ color: '#1A7A4A', fontWeight: 600, textDecoration: 'none' }}>(956) 226-3903</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={logo}>Tam<span style={{ color: '#2EA865' }}>bo</span></div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#111', marginBottom: 6, textAlign: 'center' }}>Create a new password</div>
        {userEmail && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <span style={{ fontSize: 12, background: '#f0fdf4', color: '#166534', padding: '4px 12px', borderRadius: 20, fontWeight: 600 }}>
              {userEmail}
            </span>
          </div>
        )}
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 24, textAlign: 'center' }}>
          Choose a new password for your Tambo account.
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>New Password</label>
          <div style={{ position: 'relative' }}>
            <input
              style={{ ...inputStyle, paddingRight: 40 }}
              type={showPwd ? 'text' : 'password'}
              placeholder="At least 8 characters"
              value={password}
              onChange={function(e) { setPassword(e.target.value); setMessage(''); }}
              onKeyDown={function(e) { if (e.key === 'Enter') handleSetPassword(); }}
            />
            <button type="button" onClick={function() { setShowPwd(function(v) { return !v; }); }} tabIndex={-1} aria-label={showPwd ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9ca3af', lineHeight: 0 }}>
              {showPwd
                ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Confirm Password</label>
          <div style={{ position: 'relative' }}>
            <input
              style={{ ...inputStyle, paddingRight: 40 }}
              type={showConfirm ? 'text' : 'password'}
              placeholder="Repeat your password"
              value={confirm}
              onChange={function(e) { setConfirm(e.target.value); setMessage(''); }}
              onKeyDown={function(e) { if (e.key === 'Enter') handleSetPassword(); }}
            />
            <button type="button" onClick={function() { setShowConfirm(function(v) { return !v; }); }} tabIndex={-1} aria-label={showConfirm ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9ca3af', lineHeight: 0 }}>
              {showConfirm
                ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
        </div>

        {message && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>
            {message}
          </div>
        )}

        <button
          style={{ ...btn, opacity: status === 'saving' ? 0.7 : 1, cursor: status === 'saving' ? 'not-allowed' : 'pointer' }}
          onClick={handleSetPassword}
          disabled={status === 'saving'}
        >
          {status === 'saving' ? 'Saving…' : 'Set New Password'}
        </button>
      </div>
    </div>
  );
}
